/**
 * Weighing-scale barcodes (EAN-13 with prefix 21–29):
 *   PP CCCCC VVVVV K   PP prefix, CCCCC = PLU, VVVVV = weight in grams (or price in paise), K = check digit.
 * Prefix 21–25 → weight, 26–29 → price (common store configuration; configurable per store).
 */
export interface ScaleCode {
  plu: number;
  weightGrams?: number;
  pricePaise?: number;
}

export function ean13Valid(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false;
  const digits = code.split('').map(Number);
  const sum = digits.slice(0, 12).reduce((s, d, i) => s + d * (i % 2 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === digits[12];
}

export function parseScaleBarcode(code: string, pricePrefixes = ['26', '27', '28', '29']): ScaleCode | null {
  if (!/^2[1-9]\d{11}$/.test(code) || !ean13Valid(code)) return null;
  const prefix = code.slice(0, 2);
  const plu = parseInt(code.slice(2, 7), 10);
  const value = parseInt(code.slice(7, 12), 10);
  return pricePrefixes.includes(prefix) ? { plu, pricePaise: value } : { plu, weightGrams: value };
}

/** Store-wide layout of weighing-scale labels (EAN-13 starting with 2). */
export interface ScaleLayout {
  prefixes: string;          // e.g. "20-29" or "21,22,26"
  itemDigits: number;        // PLU digits after the prefix (4–6)
  value: 'weight' | 'price'; // what the remaining digits hold
  decimals: number;          // weight: 3 → grams; price: 2 → paise
}

export function prefixList(spec: string): string[] {
  const out = new Set<string>();
  for (const part of spec.split(',').map((p) => p.trim()).filter(Boolean)) {
    const m = /^(\d{2})\s*-\s*(\d{2})$/.exec(part);
    if (m) for (let i = Number(m[1]); i <= Number(m[2]); i++) out.add(String(i).padStart(2, '0'));
    else if (/^\d{2}$/.test(part)) out.add(part);
  }
  return [...out];
}

/** Read a scale label with the store's layout (falls back to the common 21–25 weight / 26–29 price). */
export function parseScaleLabel(code: string, layout?: ScaleLayout | null): ScaleCode | null {
  if (!layout) return parseScaleBarcode(code);
  if (!/^\d{13}$/.test(code) || !ean13Valid(code) || !prefixList(layout.prefixes).includes(code.slice(0, 2))) return null;
  const plu = parseInt(code.slice(2, 2 + layout.itemDigits), 10);
  const raw = parseInt(code.slice(2 + layout.itemDigits, 12), 10);
  if (layout.value === 'weight') return { plu, weightGrams: Math.round(raw * 10 ** (3 - layout.decimals)) };
  return { plu, pricePaise: Math.round(raw * 10 ** (2 - layout.decimals)) };
}

/** Scanners may prefix a symbology identifier like ]C1 (GS1-128), ]e0 (DataBar), ]d2 (DataMatrix), ]Q3 (QR). */
export function stripAim(code: string): string {
  return code.replace(/^\][A-Za-z][0-9A-Za-z]/, '');
}

export const GS = '\u001d';

export interface Gs1 {
  gtin?: string; batch?: string; serial?: string;
  expiry?: number; bestBefore?: number; packed?: number; // epoch ms (end of day)
  weightGrams?: number; pricePaise?: number; count?: number;
}

const FIXED: Record<string, number> = { '00': 18, '01': 14, '02': 14, '11': 6, '12': 6, '13': 6, '15': 6, '16': 6, '17': 6, '20': 2 };
const yymmdd = (s: string) => {
  const y = 2000 + Number(s.slice(0, 2)), m = Number(s.slice(2, 4)), d = Number(s.slice(4, 6));
  // Day 00 means the last day of the month.
  const date = d === 0 ? new Date(y, m, 0) : new Date(y, m - 1, d);
  date.setHours(23, 59, 59, 0);
  return date.getTime();
};

/**
 * GS1 element strings from GS1-128, DataBar and DataMatrix labels: GTIN, batch, expiry, net weight,
 * price and count. Accepts the raw form (with GS separators) and the printed "(01)…(17)…" form.
 */
export function parseGs1(raw: string): Gs1 | null {
  let s = stripAim(raw.trim());
  if (s.startsWith('(')) s = s.replace(/\((\d{2,4})\)/g, (_, ai) => `${GS}${ai}`).replace(new RegExp(`^${GS}`), '');
  if (!/^(0[012]|1[0-7]|2[01]|3[0-9])/.test(s) || s.length < 16) return null;
  const out: Gs1 = {};
  let i = 0;
  let guard = 0;
  while (i < s.length && guard++ < 20) {
    if (s[i] === GS) { i++; continue; }
    const two = s.slice(i, i + 2);
    const four = s.slice(i, i + 4);
    if (FIXED[two] != null) {
      const v = s.slice(i + 2, i + 2 + FIXED[two]);
      if (v.length < FIXED[two]) return null;
      if (two === '01' || two === '02') out.gtin = v;
      else if (two === '17') out.expiry = yymmdd(v);
      else if (two === '15' || two === '16') out.bestBefore = yymmdd(v);
      else if (two === '11' || two === '13') out.packed = yymmdd(v);
      i += 2 + FIXED[two];
      continue;
    }
    if (/^3[1-6]\d\d$/.test(four) || /^39[2-3]\d$/.test(four)) {
      const v = s.slice(i + 4, i + 10);
      const dec = Number(four[3]);
      const n = Number(v) / 10 ** dec;
      if (four.startsWith('310')) out.weightGrams = Math.round(n * 1000);              // kg
      else if (four.startsWith('320')) out.weightGrams = Math.round(n * 453.59237);   // lb
      else if (four.startsWith('392')) {
        const end = s.indexOf(GS, i + 4);
        out.pricePaise = Math.round((Number(s.slice(i + 4, end === -1 ? undefined : end)) / 10 ** dec) * 100);
        i = end === -1 ? s.length : end;
        continue;
      }
      i += 10;
      continue;
    }
    // Variable length: up to the next GS or the end.
    const ai = ['10', '21', '30', '37'].find((a) => s.startsWith(a, i));
    if (!ai) break;
    const end = s.indexOf(GS, i + 2);
    const v = s.slice(i + 2, end === -1 ? undefined : end);
    if (ai === '10') out.batch = v.slice(0, 20);
    else if (ai === '21') out.serial = v.slice(0, 20);
    else out.count = Number(v) || undefined;
    i = end === -1 ? s.length : end;
  }
  return out.gtin ? out : null;
}

/** Codes that refer to the same item: GTIN-14 with leading zeros vs the EAN-13/UPC-A printed on the pack. */
export function gtinVariants(code: string): string[] {
  const d = code.replace(/^0+/, '');
  const v = new Set([code, d]);
  for (const len of [8, 12, 13, 14]) if (d.length <= len) v.add(d.padStart(len, '0'));
  if (/^0\d{7}$/.test(code)) v.add(upcEtoA(code));
  return [...v];
}

/** Expand a zero-suppressed UPC-E (8 digits) to UPC-A. */
export function upcEtoA(e: string): string {
  const [ns, d1, d2, d3, d4, d5, d6, check] = e.split('');
  const m = d6 === '0' || d6 === '1' || d6 === '2' ? `${d1}${d2}${d6}0000${d3}${d4}${d5}`
    : d6 === '3' ? `${d1}${d2}${d3}00000${d4}${d5}`
      : d6 === '4' ? `${d1}${d2}${d3}${d4}00000${d5}`
        : `${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  return `${ns}${m}${check}`;
}
